import { useState, useRef } from "react";

const SUGGESTED_EMOJIS = [
  '💬', '✨', '🌿', '💡', '🔥', '❤️', '🌈', '🕊️',
  '🌙', '⭐', '🧡', '💚', '💙', '💜', '🤍', '🖤',
  '🌸', '🌺', '🌻', '🍃', '🪴', '🎯', '🛡', '🤲',
  '🧘', '🏃', '🎵', '📖', '🧩', '🌊', '🏔', '🪐',
  '😌', '😤', '😣', '😔', '😊', '🥺', '😮', '🥰',
];

interface AddCustomTagProps {
  onAdd: (tag: string) => void;
  usedEmojis?: string[];
}

export default function AddCustomTag({ onAdd, usedEmojis = [] }: AddCustomTagProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [emoji, setEmoji] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleOpen() {
    setOpen(true);
    setShowEmojiPicker(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function handleConfirm() {
    const trimmed = text.trim();
    if (!trimmed) return;
    const tag = emoji ? `${emoji} ${trimmed}` : trimmed;
    onAdd(tag);
    setText("");
    setEmoji("");
    setShowEmojiPicker(false);
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      handleConfirm();
    } else if (e.key === "Escape") {
      setText("");
      setEmoji("");
      setShowEmojiPicker(false);
      setOpen(false);
    }
  }

  if (!open) {
    return (
      <div
        className="tag tag-add-own"
        onClick={handleOpen}
        style={{ cursor: "pointer", borderStyle: "dashed", opacity: 0.75 }}
      >
        + Add your own
      </div>
    );
  }

  const availableEmojis = SUGGESTED_EMOJIS.filter(e => !usedEmojis.includes(e));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {/* eslint-disable-next-line cw/no-inline-button-style -- AddCustomTag emoji-picker trigger — 44×44 icon-only swatch button, not a chamfered .btn variant */}
        <button
          onClick={() => setShowEmojiPicker(v => !v)}
          style={{ width: 44, height: 44, borderRadius: 'var(--radius-sm)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 20%, transparent)', background: emoji ? 'var(--color-bg-surface)' : 'var(--color-bg-surface)', fontSize: 'var(--fs-md)', cursor: 'pointer', flexShrink: 0 }}
          title="Pick an emoji"
        >
          {emoji || '😊'}
        </button>
        <input
          ref={inputRef}
          type="text"
          placeholder="Type your label…"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={40}
          style={{ flex: 1, fontSize: 'var(--fs-sm)', padding: '7px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 15%, transparent)', background: 'var(--color-bg-surface)', color: 'var(--color-text-primary)' }}
        />
        {/* eslint-disable-next-line cw/no-inline-button-style -- AddCustomTag Add — readiness-state pill (active/disabled) inline with input */}
        <button
          onClick={handleConfirm}
          disabled={!text.trim()}
          style={{ padding: '7px 14px', borderRadius: 'var(--radius-sm)', background: text.trim() ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-text-primary) 5%, transparent)', color: text.trim() ? '#fff' : 'var(--color-text-secondary)', border: 'none', fontSize: 'var(--fs-sm)', fontWeight: 600, cursor: text.trim() ? 'pointer' : 'default', flexShrink: 0 }}
        >
          Add
        </button>
        {/* eslint-disable-next-line cw/no-inline-button-style -- AddCustomTag close × — 44×44 transparent icon button, not a chamfered .btn variant */}
        <button
          onClick={() => { setText(""); setEmoji(""); setShowEmojiPicker(false); setOpen(false); }}
          style={{ width: 44, height: 44, borderRadius: 'var(--radius-sm)', border: 'none', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-base)', cursor: 'pointer', flexShrink: 0 }}
        >
          ✕
        </button>
      </div>
      {showEmojiPicker && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 0 2px' }}>
          {availableEmojis.map(e => (
            // eslint-disable-next-line cw/no-inline-button-style -- AddCustomTag emoji-swatch in picker — 44×44 selected/unselected swatch
            <button
              key={e}
              onClick={() => { setEmoji(e); setShowEmojiPicker(false); }}
              title={usedEmojis.includes(e) ? 'Already used in this step' : undefined}
              style={{ width: 44, height: 44, borderRadius: 'var(--radius-sm)', border: emoji === e ? '2px solid var(--color-accent)' : '1px solid color-mix(in srgb, var(--color-text-primary) 10%, transparent)', background: emoji === e ? 'var(--color-bg-surface)' : 'color-mix(in srgb, var(--color-text-primary) 2%, transparent)', fontSize: 'var(--fs-md)', cursor: 'pointer' }}
            >
              {e}
            </button>
          ))}
          {/* eslint-disable-next-line cw/no-inline-button-style -- AddCustomTag no-emoji option — short pill at end of emoji grid */}
          <button
            onClick={() => { setEmoji(""); setShowEmojiPicker(false); }}
            style={{ padding: '0 10px', borderRadius: 'var(--radius-sm)', border: '1px solid color-mix(in srgb, var(--color-text-primary) 10%, transparent)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-xs)', cursor: 'pointer', height: 44 }}
          >
            No emoji
          </button>
        </div>
      )}
    </div>
  );
}
